import { useCallback, useEffect, useRef, useState } from "react";
import { ActionList, ActionMenu, Breadcrumbs, IconButton, NavList } from "@primer/react";
import { FeatureFlags } from "@primer/react/experimental";
import { SyncIcon, ThreeBarsIcon, TriangleUpIcon, XIcon } from "@primer/octicons-react";
import { Button } from "../Button/Button.jsx";
import { ProfileIdentity } from "../ProfileIdentity/ProfileIdentity.jsx";
import { sortProfilesByName } from "../../lib/sortProfilesByName.js";
import "./AppShell.css";

function groupedPages(pages) {
  return pages.reduce((groups, page) => {
    const group = groups.find((item) => item.label === page.group);
    if (group) {
      group.pages.push(page);
    } else {
      groups.push({ label: page.group, pages: [page] });
    }
    return groups;
  }, []);
}

function NavigationLink({ current, onNavigate, page }) {
  const Icon = page.icon;
  return (
    <NavList.Item
      className={current ? "active" : ""}
      href={`#/${page.key}`}
      aria-current={current ? "page" : undefined}
      onClick={onNavigate}
    >
      <NavList.LeadingVisual><Icon aria-hidden="true" /></NavList.LeadingVisual>
      <span>{page.label}</span>
    </NavList.Item>
  );
}

function ProfileMenu({ application, onNavigate }) {
  const profile = application?.profile;

  return <FeatureFlags flags={{ primer_react_css_anchor_positioning: true }}><ActionMenu>
    <ActionMenu.Button
      className="profile-menu"
      variant="invisible"
      alignContent="start"
      disabled={!profile || application.saving}
      trailingAction={TriangleUpIcon}
      aria-label={profile ? `Current profile: ${profile.name}` : "Loading profile"}
    >{profile ? <ProfileIdentity profile={profile} /> : "Loading profile…"}</ActionMenu.Button>
    <ActionMenu.Overlay align="start" side="outside-top">
      <ActionList selectionVariant="single">
        {sortProfilesByName(application?.profiles ?? []).map((item) => <ActionList.Item
          selected={item.id === profile?.id}
          onSelect={async () => {
            await application.switchProfile(item.id);
            onNavigate();
          }}
          key={item.id}
        ><ProfileIdentity profile={item} /></ActionList.Item>)}
      </ActionList>
    </ActionMenu.Overlay>
  </ActionMenu></FeatureFlags>;
}

function BreadcrumbMenuItem({ crumb, option }) {
  const Icon = option.icon;
  return <ActionList.Item
    selected={option.id === crumb.id}
    onSelect={() => { window.location.hash = option.href; }}
  >{Icon ? <ActionList.LeadingVisual><Icon aria-hidden="true" /></ActionList.LeadingVisual> : null}{option.label}{option.description ? <ActionList.Description variant="block">{option.description}</ActionList.Description> : null}</ActionList.Item>;
}

function containBreadcrumbMenuScroll(event) {
  const scrollContainer = event.currentTarget.querySelector("[data-overflow-auto]") ?? event.currentTarget;
  const atStart = scrollContainer.scrollTop <= 0;
  const atEnd = Math.ceil(scrollContainer.scrollTop + scrollContainer.clientHeight) >= scrollContainer.scrollHeight;
  const cannotScroll = scrollContainer.scrollHeight <= scrollContainer.clientHeight;
  if (cannotScroll || (event.deltaY < 0 && atStart) || (event.deltaY > 0 && atEnd)) event.preventDefault();
}

function BreadcrumbMenu({ crumb }) {
  const optionGroups = crumb.optionGroups ?? [{ options: crumb.options }];
  const [overlayElement, setOverlayElement] = useState(null);

  useEffect(() => {
    if (!overlayElement) return undefined;
    overlayElement.addEventListener("wheel", containBreadcrumbMenuScroll, { passive: false });
    return () => overlayElement.removeEventListener("wheel", containBreadcrumbMenuScroll);
  }, [overlayElement]);

  return <FeatureFlags flags={{ primer_react_css_anchor_positioning: true }}><ActionMenu>
    <ActionMenu.Button className="breadcrumb-menu" variant="invisible" size="small">{crumb.label}</ActionMenu.Button>
    <ActionMenu.Overlay className="breadcrumb-menu-overlay" align="start" maxHeight={crumb.optionGroups ? "large" : "medium"} overflow="auto" ref={setOverlayElement}>
      <ActionList selectionVariant="single">
        {optionGroups.map((group) => <ActionList.Group key={group.label ?? "options"}>
          {optionGroups.length > 1 ? <ActionList.GroupHeading>{group.label}</ActionList.GroupHeading> : null}
          {group.options.map((option) => <BreadcrumbMenuItem crumb={crumb} option={option} key={option.id} />)}
        </ActionList.Group>)}
      </ActionList>
    </ActionMenu.Overlay>
  </ActionMenu></FeatureFlags>;
}

function HeaderBreadcrumbs({ breadcrumbs }) {
  return <Breadcrumbs className="header-breadcrumbs" overflow="menu" variant="spacious">
    {breadcrumbs.map((crumb, index) => {
      const current = index === breadcrumbs.length - 1;
      if (current && (crumb.options?.length || crumb.optionGroups?.length)) return <Breadcrumbs.Item as="span" className="record-crumb" selected key={`${crumb.label}:${index}`}><BreadcrumbMenu crumb={crumb} /></Breadcrumbs.Item>;
      if (crumb.href) return <Breadcrumbs.Item href={crumb.href} selected={current} key={`${crumb.label}:${index}`}>{crumb.label}</Breadcrumbs.Item>;
      return <Breadcrumbs.Item as="span" selected={current} key={`${crumb.label}:${index}`}>{crumb.label}</Breadcrumbs.Item>;
    })}
  </Breadcrumbs>;
}

export function AppShell({ application, breadcrumbs, children, currentPage, onSync, pages, syncEnabled = false }) {
  const [navigationOpen, setNavigationOpen] = useState(false);
  const menuButtonRef = useRef(null);
  const sidebarRef = useRef(null);
  const navigationPages = pages.filter((page) => page.navigation !== false);
  const pageGroups = groupedPages(navigationPages);
  const routeBreadcrumbs = breadcrumbs ?? [{ label: currentPage.label }];
  const headerBreadcrumbs = routeBreadcrumbs.length === 1 && currentPage.navigation !== false
    ? [{
      ...routeBreadcrumbs[0],
      id: currentPage.key,
      optionGroups: pageGroups.map((group) => ({
        label: group.label,
        options: group.pages.map((page) => ({ href: `#/${page.key}`, icon: page.icon, id: page.key, label: page.label }))
      }))
    }]
    : routeBreadcrumbs;

  const closeNavigation = useCallback(({ restoreFocus = true } = {}) => {
    setNavigationOpen(false);
    if (restoreFocus) menuButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!navigationOpen) return undefined;

    const focusable = sidebarRef.current?.querySelectorAll("a, button, select") ?? [];
    focusable[0]?.focus();

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        event.preventDefault();
        closeNavigation();
        return;
      }

      if (event.key !== "Tab" || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.body.classList.add("navigation-open");
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.classList.remove("navigation-open");
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeNavigation, navigationOpen]);

  return (
    <div className="app-shell">
      <button
        className={`navigation-backdrop${navigationOpen ? " visible" : ""}`}
        type="button"
        aria-label="Close navigation"
        tabIndex={navigationOpen ? 0 : -1}
        onClick={() => closeNavigation()}
      />
      <aside
        id="primary-navigation"
        className={`sidebar${navigationOpen ? " open" : ""}`}
        aria-label="Primary navigation"
        ref={sidebarRef}
      >
        <div className="brand">
          <span className="brand-mark"><img src="/icons/rupee-mark.svg" alt="" /></span>
          <span className="brand-name">Regulator</span>
          <small>Portfolio Compliance</small>
          <IconButton
            className="close-navigation"
            icon={XIcon}
            aria-label="Close navigation"
            onClick={() => closeNavigation()}
          />
        </div>
        <NavList className="navigation-list" aria-label="Regulator sections">
          <NavList.Heading visuallyHidden>Regulator sections</NavList.Heading>
          {pageGroups.map((group) => (
            <NavList.Group title={group.label} key={group.label}>
              {group.pages.map((page) => (
                <NavigationLink
                  key={page.key}
                  page={page}
                  current={page.key === currentPage.key}
                  onNavigate={() => closeNavigation({ restoreFocus: false })}
                />
              ))}
            </NavList.Group>
          ))}
        </NavList>
        <div className="sidebar-profile">
          <ProfileMenu application={application} onNavigate={() => closeNavigation({ restoreFocus: false })} />
        </div>
      </aside>
      <div className="app-frame">
        <header className="app-header">
          <div className="app-header-content">
            <IconButton
              className="menu-button"
              icon={ThreeBarsIcon}
              aria-controls="primary-navigation"
              aria-expanded={navigationOpen}
              aria-label="Open navigation"
              ref={menuButtonRef}
              onClick={() => setNavigationOpen(true)}
            />
            <HeaderBreadcrumbs breadcrumbs={headerBreadcrumbs} />
            {syncEnabled ? <Button
              className="header-sync"
              tone="secondary"
              leadingVisual={SyncIcon}
              aria-label={`Sync ${application.profile?.name ?? "profile"}`}
              onClick={(event) => onSync?.({ keyboard: event.detail === 0, returnFocusTarget: event.currentTarget })}
            ><span className="header-sync-label">Sync</span></Button> : null}
          </div>
        </header>
        <main id="main-content" className="app-main" tabIndex="-1">
          {children}
        </main>
      </div>
    </div>
  );
}
